import { useState, useEffect } from 'react';
import { ChevronDown, Plus, Check, DollarSign, Package } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import CreateScenarioDialog from './CreateScenarioDialog';
import ScenarioActionsMenu from './ScenarioActionsMenu';

export default function ScenarioSelector({
  scenarios,
  currentScenario,
  onScenarioChange,
  onCreateScenario,
  onUpdate,
  partNumber,
  programId
}) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [activeScenario, setActiveScenario] = useState(null);

  useEffect(() => {
    const active = scenarios.find(s => s.is_active);
    setActiveScenario(active);
  }, [scenarios]);

  const formatCurrency = (cents) => {
    if (!cents) return '$0';
    const value = cents / 100;
    if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const getDeltaLines = (scenario) => {
    if (!activeScenario || scenario.id === activeScenario.id) return [];
    const lines = [];
    const revenueDelta = scenario.revenue_delta_vs_active_cents || 0;
    const tariffDelta = scenario.tariff_delta_vs_active_cents || 0;

    if (revenueDelta > 0) {
      lines.push({ label: `Revenue upside: ${formatCurrency(revenueDelta)} vs Live`, tone: 'text-green-600' });
    } else if (revenueDelta < 0) {
      lines.push({ label: `Revenue downside: ${formatCurrency(Math.abs(revenueDelta))} vs Live`, tone: 'text-red-600' });
    }

    if (tariffDelta > 0) {
      lines.push({ label: `Cost increase: ${formatCurrency(tariffDelta)} vs Live`, tone: 'text-red-600' });
    } else if (tariffDelta < 0) {
      lines.push({ label: `Cost reduction: ${formatCurrency(Math.abs(tariffDelta))} vs Live`, tone: 'text-green-600' });
    }

    if (lines.length === 0) {
      lines.push({ label: 'No revenue or cost change vs Live', tone: 'text-gray-500' });
    }

    return lines;
  };

  const handleCreateSuccess = (newScenario) => {
    setShowCreateDialog(false);
    onCreateScenario(newScenario);
  };

  const comparisonUrl = programId
    ? `/parts/${partNumber}/compare?programId=${encodeURIComponent(programId)}`
    : `/parts/${partNumber}/compare`;

  return (
    <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className="text-sm font-medium text-gray-600 shrink-0">Scenario:</span>

        {currentScenario && (
          <ScenarioActionsMenu
            scenario={currentScenario}
            partNumber={partNumber}
            programId={programId}
            onUpdate={onUpdate}
          />
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="justify-between bg-white hover:bg-gray-50 min-w-[250px]"
            >
              <div className="flex items-center gap-2 overflow-hidden">
                <span className="font-medium truncate">
                  {currentScenario?.name || 'Select Scenario'}
                </span>
                {currentScenario?.is_active && (
                  <Badge variant="default" className="bg-green-600 text-xs">LIVE</Badge>
                )}
              </div>
              <ChevronDown className="h-4 w-4 shrink-0 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[440px]" align="start">
            <DropdownMenuLabel className="text-xs text-gray-500">
              Select Scenario ({scenarios.length} total)
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            {scenarios.map((scenario) => {
              const deltaLines = getDeltaLines(scenario);
              const isSelected = currentScenario?.id === scenario.id;

              return (
                <DropdownMenuItem
                  key={scenario.id}
                  onClick={() => onScenarioChange(scenario)}
                  className={`flex items-start gap-3 p-3 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                >
                  <div className="flex items-center justify-center w-5 h-5 mt-0.5">
                    {isSelected && <Check className="h-4 w-4 text-blue-600" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">{scenario.name}</span>
                      {scenario.is_active && (
                        <Badge variant="default" className="bg-green-600 text-xs">LIVE</Badge>
                      )}
                      {scenario.status === 'draft' && (
                        <Badge variant="outline" className="text-xs">DRAFT</Badge>
                      )}
                      {scenario.status === 'archived' && (
                        <Badge variant="secondary" className="text-xs">ARCHIVED</Badge>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        Revenue: {formatCurrency(scenario.total_revenue_cents)}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        Tariff: {formatCurrency(scenario.total_tariff_cost_cents)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Package className="h-3 w-3" />
                        {scenario.total_volume?.toLocaleString() || 0} units
                      </span>
                    </div>

                    {deltaLines.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {deltaLines.map((line) => (
                          <div key={line.label} className={`text-xs font-medium ${line.tone}`}>
                            {line.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </DropdownMenuItem>
              );
            })}

            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setShowCreateDialog(true)}
              className="flex items-center gap-2 p-3 text-blue-600 cursor-pointer hover:bg-blue-50"
            >
              <Plus className="h-4 w-4" />
              <span className="font-medium">Create New Scenario</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {currentScenario && scenarios.length > 1 && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.location.href = comparisonUrl}
          className="shrink-0"
        >
          Compare All
        </Button>
      )}

      <CreateScenarioDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={handleCreateSuccess}
        partNumber={partNumber}
        programId={programId}
        scenarios={scenarios}
      />
    </div>
  );
}
